import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { useAuth } from "~/lib/hooks/useAuth";
import { cn } from "~/lib/utils";

const formSchema = z.object({
	email: z.string().email("Please enter a valid email address"),
});

function Login() {
	const navigate = useNavigate();
	const { sendOTP } = useAuth();
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
		},
	});

	const search = Route.useSearch();

	async function onSubmit(values: z.infer<typeof formSchema>) {
		await sendOTP.mutateAsync(values.email);
		navigate({
			to: "/verify",
			search: {
				...search,
				email: values.email,
				choice: search.choice,
			},
		});
	}

	return (
		<div className="min-h-screen w-full bg-black flex items-center justify-center p-4 sm:px-6 lg:px-8">
			<div className="w-full max-w-md">
				<div className="text-center mb-10">
					<h1 className="text-5xl sm:text-7xl leading-none font-black text-transparent bg-clip-text bg-gradient-to-br from-white to-accent/80 tracking-wide font-heading">
						meteor
					</h1>
					<p className="text-secondary uppercase tracking-widest mt-3 font-medium">
						Make Memories
					</p>
				</div>
				<Card className="bg-primary/10 border-white/10 backdrop-blur-sm shadow-2xl shadow-accent/20">
					<CardHeader className="space-y-2 pt-8 pb-4">
						<h2 className="text-2xl font-semibold tracking-tight text-center text-primary-foreground">
							Get Started
						</h2>
						<p className="text-base text-secondary text-center">
							Login or sign up with your email
						</p>
					</CardHeader>
					<CardContent className="space-y-6 pb-8 px-8">
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onSubmit)}
								className="space-y-6"
							>
								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FormItem>
											<div className="flex gap-3">
												{/* <div className="flex h-10 items-center gap-2 px-3 rounded-md text-secondary border w-[5.5rem] justify-center sm:justify-start shrink-0 bg-primary/10 border-white/10 text-white">
													<span className="text-lg">🇮🇳</span>
													<span>+91</span>
												</div> */}
												<FormControl>
													<Input
														placeholder="hello@withmeteor.com"
														type="email"
														className={cn(
															"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20 h-10",
															sendOTP.isPending && "opacity-50",
														)}
														{...field}
														disabled={sendOTP.isPending}
													/>
												</FormControl>
											</div>
											<FormMessage className="mt-2 text-red-500 text-sm" />
										</FormItem>
									)}
								/>
								<Button
									type="submit"
									className="w-full h-10 text-lg bg-accent hover:bg-accent/90 text-accent-foreground"
									disabled={sendOTP.isPending}
								>
									{sendOTP.isPending ? "Sending..." : "Continue"}
								</Button>
							</form>
						</Form>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/_auth/login")({
	component: Login,
});
